/**
 * Remediation integrations (/audit/advanced/integrations): connect / test / disconnect the providers that can apply
 * approved fixes - WordPress (Quixo connector plugin + application password), Google Business Profile (OAuth),
 * Cloudflare (scoped API token, exact permissions listed), Meta (OAuth). Shows status, scopes, last check, health and
 * which findings of the audit (?job=) each provider could fix. Secrets are sent once to the vault and never shown
 * again (spec rule 5). Only owners/admins manage connections.
 */
import { useId, useState, type FormEvent } from 'react';
import { REMEDIATION_PROVIDERS, type AdvancedFinding, type ProviderConnection, type RemediationProviderId } from '@qm/contracts/audit-remediation';
import { Badge, Button, ErrorState, Heading, Loading } from '@qm/ui';
import { useApp } from '../../../../../lib/app-context';
import { useFlags, type FlagState } from '../../../../../lib/flags';
import { useFormat } from '../../../../../lib/format';
import { safeAuthorizeUrl } from '../../../integrations/IntegrationsPage';
import { actionErrorKey, OAUTH_CONNECT_PATH, type ConnectResult, type RemediationClient } from '../actions/client';
import { AdvancedPage, AUDIT_FLAGS, FailureState, providerFlag, useJobParam, useLoad, useRemediationClient, useRoles, type Load } from '../actions/context';
import { connectionFor } from '../actions/resolve-flow';
import { lt } from '../actions/ResolvePanel';
import '../actions/actions.css';
import { BrandLogo } from '../../../../../shell/BrandLogo';

export const CLOUDFLARE_PERMISSIONS = ['zoneRead', 'zoneSettingsEdit', 'transformRulesEdit', 'cacheRulesEdit'] as const;
export const WORDPRESS_STEPS = ['plugin', 'activate', 'user', 'password', 'paste'] as const;
const OAUTH: readonly RemediationProviderId[] = ['gbp', 'meta'];

export type WpForm = { siteUrl: string; username: string; applicationPassword: string; signingSecret: string };
export type CfForm = { zoneName: string; apiToken: string };
type Errors<T> = Partial<Record<keyof T, string>>;

/** https site root only; app passwords are 24 letters/digits (WordPress shows them in groups of 4). */
export function validateWordPress(f: WpForm): Errors<WpForm> {
  const e: Errors<WpForm> = {};
  try { const u = new URL(f.siteUrl.trim()); if (u.protocol !== 'https:') e.siteUrl = 'audit-remediation.integrations.errors.https'; }
  catch { e.siteUrl = 'audit-remediation.integrations.errors.url'; }
  if (!f.username.trim()) e.username = 'audit-remediation.integrations.errors.required';
  if (!/^[A-Za-z0-9]{24}$/.test(f.applicationPassword.replace(/\s+/g, ''))) e.applicationPassword = 'audit-remediation.integrations.errors.appPassword';
  if (!/^[a-fA-F0-9]{64}$/.test(f.signingSecret.trim())) e.signingSecret = 'audit-remediation.integrations.errors.signingSecret';
  return e;
}
export function validateCloudflare(f: CfForm): Errors<CfForm> {
  const e: Errors<CfForm> = {};
  if (!/^(?=.{3,253}$)([a-z0-9-]+\.)+[a-z]{2,}$/i.test(f.zoneName.trim())) e.zoneName = 'audit-remediation.integrations.errors.zone';
  if (!/^[A-Za-z0-9_-]{30,400}$/.test(f.apiToken.trim())) e.apiToken = 'audit-remediation.integrations.errors.token';
  return e;
}

/** Validate, then send to the provider's connect endpoint. Never keeps the secret after the call. */
export async function submitConnect(client: RemediationClient, provider: RemediationProviderId, form: WpForm | CfForm | null, returnTo: string):
  Promise<{ errors: Record<string, string> } | { result: ConnectResult } | { error: string } | { navigate: string }> {
  try {
    if (provider === 'wordpress') {
      const f = form as WpForm; const errors = validateWordPress(f);
      if (Object.keys(errors).length) return { errors: errors as Record<string, string> };
      return { result: await client.connect({ provider, body: { siteUrl: f.siteUrl.trim(), username: f.username.trim(), applicationPassword: f.applicationPassword.replace(/\s+/g, ''), signingSecret: f.signingSecret.trim().toLowerCase() } }) };
    }
    if (provider === 'cloudflare') {
      const f = form as CfForm; const errors = validateCloudflare(f);
      if (Object.keys(errors).length) return { errors: errors as Record<string, string> };
      return { result: await client.connect({ provider, body: { domain: f.zoneName.trim().toLowerCase(), token: f.apiToken.trim() } }) };
    }
    // GBP / Meta reuse the Google / Meta account connected under Integrations (OAuth there), then pick the location / Page
    return { navigate: `${OAUTH_CONNECT_PATH}?provider=${provider}&returnTo=${encodeURIComponent(returnTo)}` };
  } catch (e) { return { error: actionErrorKey(e) }; }
}

/** Findings of the audit this provider can fix (auto) or would turn into one-click fixes once connected. */
export const fixableBy = (findings: readonly AdvancedFinding[], p: RemediationProviderId) =>
  findings.filter((f) => f.resolvability.provider === p && !['verified', 'dismissed', 'accepted_risk'].includes(f.status));

export function AdvancedIntegrationsPage() {
  return <AdvancedPage area="integrations" flag={AUDIT_FLAGS.remediation}><IntegrationsScreen /></AdvancedPage>;
}

function IntegrationsScreen() {
  const job = useJobParam();
  const flags = useFlags();
  const cons = useLoad('connections', (c) => c.connections());
  const report = useLoad(job ? `report:${job}` : null, (c) => c.report(job!));
  return <IntegrationsView connections={cons} findings={job && report.status === 'ready' ? report.data.findings : null} flag={flags}
    onChanged={(list) => cons.set(list)} />;
}

export function IntegrationsView({ connections, findings, flag, onChanged }: {
  connections: Load<ProviderConnection[]>; findings: readonly AdvancedFinding[] | null; flag: (k: string) => FlagState; onChanged: (list: ProviderConnection[]) => void;
}) {
  const { t } = useApp();
  if (connections.status === 'loading') return <Loading label={t('common.loading')} />;
  if (connections.status === 'failed') return <FailureState failure={connections.failure} />;
  const list = connections.data;
  const replace = (c: ProviderConnection) => onChanged([...list.filter((x) => x.id !== c.id), c]);
  return (
    <ul className="ar-providers">
      {REMEDIATION_PROVIDERS.map((p) => (
        <ProviderCard key={p} provider={p} connection={connectionFor(p, list)} findings={findings} available={flag(providerFlag(p)) === 'on'} onConnection={replace} />
      ))}
    </ul>
  );
}

function ProviderCard({ provider, connection, findings, available, onConnection }: {
  provider: RemediationProviderId; connection: ProviderConnection | null; findings: readonly AdvancedFinding[] | null; available: boolean; onConnection: (c: ProviderConnection) => void;
}) {
  const { t, locale } = useApp();
  const f = useFormat();
  const roles = useRoles();
  const client = useRemediationClient();
  const id = useId();
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const name = t(`audit-remediation.provider.${provider}`);
  const linked = connection && connection.status !== 'revoked' ? connection : null;
  const fixes = findings ? fixableBy(findings, provider) : null;

  const act = async (fn: () => Promise<ProviderConnection>, ok: string) => {
    setBusy(true); setError(null); setNotice(null);
    try { onConnection(await fn()); setNotice(ok); setConfirm(false); } catch (e) { setError(actionErrorKey(e)); } finally { setBusy(false); }
  };
  return (
    <li className="ar-provider" id={`provider-${provider}`} aria-labelledby={`${id}-h`} data-testid={`provider-${provider}`}>
      <div className="ar-provider__head">
        <span className="qm-brand-title"><BrandLogo provider={provider} size={28} decorative /><Heading level={2} id={`${id}-h`} className="ar-card__title">{name}</Heading></span>
        {!available ? <Badge>{t('audit-remediation.integrations.soon')}</Badge>
          : linked ? <Badge tone={linked.status === 'connected' ? 'success' : 'warning'}>{t(`audit-remediation.connectionStatus.${linked.status}`)}</Badge>
            : <Badge>{t('audit-remediation.integrations.notConnected')}</Badge>}
      </div>
      <p className="ar-muted">{t(`audit-remediation.integrations.about.${provider}`)}</p>
      {linked ? (
        <ul className="qs-kv">
          <li><span className="qs-kv__label">{t('audit-remediation.integrations.account')}</span><span className="qs-kv__value"><bdi>{f.text(linked.accountLabel)}</bdi> · <bdi dir="ltr">{f.text(linked.accountRef)}</bdi></span></li>
          <li><span className="qs-kv__label">{t('audit-remediation.integrations.scopes')}</span><span className="qs-kv__value">{linked.scopes.length ? linked.scopes.map((s) => <code key={s} className="ar-scope" dir="ltr">{s}</code>) : '—'}</span></li>
          <li><span className="qs-kv__label">{t('audit-remediation.integrations.lastUsed')}</span><span className="qs-kv__value">{linked.lastCheckedAt ? f.dateTime(linked.lastCheckedAt) : t('audit-remediation.integrations.never')}</span></li>
          <li><span className="qs-kv__label">{t('audit-remediation.integrations.health')}</span><span className="qs-kv__value">
            {linked.status === 'connected' && !linked.lastErrorCode ? t('audit-remediation.integrations.healthy') : t('audit-remediation.integrations.unhealthy', { code: linked.lastErrorCode ?? linked.status })}</span></li>
        </ul>
      ) : null}
      {fixes ? (
        <div className="ar-fixes">
          <p>{t('audit-remediation.integrations.fixes', { n: fixes.length })}</p>
          {fixes.length ? <ul>{fixes.slice(0, 5).map((x) => <li key={x.id}><bdi>{f.text(lt(x.title, locale))}</bdi></li>)}</ul> : null}
        </div>
      ) : null}
      <p role="status" aria-live="polite" className="ar-muted">{notice ? t(notice) : ''}</p>
      {error ? <ErrorState message={t(error)} /> : null}
      {available && roles.manage ? (
        <div className="cu-btnrow">
          {!linked || linked.status !== 'connected' ? (
            <Button variant="accent" aria-expanded={open} aria-controls={`${id}-form`} onClick={() => setOpen((v) => !v)} data-testid={`connect-${provider}`}>
              {t(linked ? 'audit-remediation.integrations.reconnect' : 'audit-remediation.integrations.connect', { provider: name })}
            </Button>
          ) : null}
          {linked ? <Button disabled={busy} onClick={() => void act(() => client.testConnection(provider, linked.id), 'audit-remediation.integrations.tested')}>{t('audit-remediation.integrations.test')}</Button> : null}
          {linked && !confirm ? <Button disabled={busy} onClick={() => setConfirm(true)} data-testid={`disconnect-${provider}`}>{t('audit-remediation.integrations.disconnect')}</Button> : null}
        </div>
      ) : available && !roles.manage ? <p className="ar-muted">{t('audit-remediation.integrations.ownerOnly')}</p> : null}
      {linked && confirm ? (
        <div className="ar-callout" role="alertdialog" aria-labelledby={`${id}-dc`}>
          <p id={`${id}-dc`}>{t('audit-remediation.integrations.disconnectConfirm', { provider: name })}</p>
          <div className="cu-btnrow">
            <Button variant="accent" disabled={busy} onClick={() => void act(() => client.disconnect(provider, linked.id), 'audit-remediation.integrations.disconnected')} data-testid={`confirm-disconnect-${provider}`}>{t('audit-remediation.integrations.disconnect')}</Button>
            <Button disabled={busy} onClick={() => setConfirm(false)}>{t('audit-remediation.actions.cancel')}</Button>
          </div>
        </div>
      ) : null}
      <div id={`${id}-form`} hidden={!open}>{open ? <ConnectForm provider={provider} onDone={(c) => { onConnection(c); setOpen(false); setNotice('audit-remediation.integrations.connected'); }} /> : null}</div>
    </li>
  );
}

export function ConnectForm({ provider, onDone, navigate = (u) => window.location.assign(u) }: {
  provider: RemediationProviderId; onDone: (c: ProviderConnection) => void; navigate?: (url: string) => void;
}) {
  const { t } = useApp();
  const client = useRemediationClient();
  const id = useId();
  const [wp, setWp] = useState<WpForm>({ siteUrl: '', username: '', applicationPassword: '', signingSecret: '' });
  const [cf, setCf] = useState<CfForm>({ zoneName: '', apiToken: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault(); setBusy(true); setError(null);
    const out = await submitConnect(client, provider, provider === 'wordpress' ? wp : provider === 'cloudflare' ? cf : null, '/audit/advanced/integrations');
    setBusy(false);
    if ('errors' in out) return setErrors(out.errors);
    setErrors({});
    if ('error' in out) return setError(out.error);
    if ('navigate' in out) return navigate(out.navigate);
    // secrets leave component state as soon as the vault has them
    setWp({ siteUrl: '', username: '', applicationPassword: '', signingSecret: '' }); setCf({ zoneName: '', apiToken: '' });
    if (out.result.connection) return onDone(out.result.connection);
    const url = safeAuthorizeUrl(out.result.authorizeUrl);
    if (url) navigate(url); else setError('audit-remediation.actions.errors.generic');
  };
  const field = (name: string, label: string, value: string, set: (v: string) => void, opts: { type?: string; hint?: string; autoComplete?: string } = {}) => (
    <div className="cu-field">
      <label htmlFor={`${id}-${name}`}>{t(label)}</label>
      <input id={`${id}-${name}`} name={name} type={opts.type ?? 'text'} dir="ltr" autoComplete={opts.autoComplete ?? 'off'} spellCheck={false} value={value} onChange={(e) => set(e.target.value)}
        aria-invalid={errors[name] ? true : undefined} aria-describedby={[opts.hint ? `${id}-${name}-h` : '', errors[name] ? `${id}-${name}-e` : ''].filter(Boolean).join(' ') || undefined} />
      {opts.hint ? <p className="ar-muted" id={`${id}-${name}-h`}>{t(opts.hint)}</p> : null}
      {errors[name] ? <p className="qm-error" role="alert" id={`${id}-${name}-e`}>{t(errors[name]!)}</p> : null}
    </div>
  );
  return (
    <form className="ar-connect" onSubmit={(e) => void submit(e)} aria-label={t('audit-remediation.integrations.formLabel', { provider: t(`audit-remediation.provider.${provider}`) })} data-testid={`form-${provider}`}>
      {provider === 'wordpress' ? (
        <>
          <ol className="ar-howto">{WORDPRESS_STEPS.map((s) => <li key={s}>{t(`audit-remediation.integrations.wordpress.step.${s}`)}</li>)}</ol>
          {field('siteUrl', 'audit-remediation.integrations.wordpress.siteUrl', wp.siteUrl, (v) => setWp({ ...wp, siteUrl: v }), { type: 'url', hint: 'audit-remediation.integrations.wordpress.siteUrlHint', autoComplete: 'url' })}
          {field('username', 'audit-remediation.integrations.wordpress.username', wp.username, (v) => setWp({ ...wp, username: v }), { autoComplete: 'username' })}
          {field('applicationPassword', 'audit-remediation.integrations.wordpress.appPassword', wp.applicationPassword, (v) => setWp({ ...wp, applicationPassword: v }), { type: 'password', hint: 'audit-remediation.integrations.wordpress.appPasswordHint', autoComplete: 'new-password' })}
          {field('signingSecret', 'audit-remediation.integrations.wordpress.signingSecret', wp.signingSecret, (v) => setWp({ ...wp, signingSecret: v }), { type: 'password', hint: 'audit-remediation.integrations.wordpress.signingSecretHint', autoComplete: 'off' })}
        </>
      ) : provider === 'cloudflare' ? (
        <>
          <p>{t('audit-remediation.integrations.cloudflare.intro')}</p>
          <ol className="ar-howto">
            <li>{t('audit-remediation.integrations.cloudflare.step.create')}</li>
            <li>{t('audit-remediation.integrations.cloudflare.step.permissions')}
              <ul className="ar-perms" data-testid="cf-permissions">{CLOUDFLARE_PERMISSIONS.map((p) => <li key={p}><code dir="ltr">{t(`audit-remediation.integrations.cloudflare.perm.${p}`)}</code></li>)}</ul>
            </li>
            <li>{t('audit-remediation.integrations.cloudflare.step.resources')}</li>
            <li>{t('audit-remediation.integrations.cloudflare.step.copy')}</li>
          </ol>
          {field('zoneName', 'audit-remediation.integrations.cloudflare.zone', cf.zoneName, (v) => setCf({ ...cf, zoneName: v }), { hint: 'audit-remediation.integrations.cloudflare.zoneHint' })}
          {field('apiToken', 'audit-remediation.integrations.cloudflare.token', cf.apiToken, (v) => setCf({ ...cf, apiToken: v }), { type: 'password', hint: 'audit-remediation.integrations.cloudflare.tokenHint' })}
        </>
      ) : (
        <p>{t(`audit-remediation.integrations.oauth.${OAUTH.includes(provider) ? provider : 'gbp'}`)}</p>
      )}
      <p className="ar-muted">{t('audit-remediation.integrations.secretNote')}</p>
      {error ? <ErrorState message={t(error)} /> : null}
      <div className="cu-btnrow"><Button type="submit" variant="accent" disabled={busy}>{t(OAUTH.includes(provider) ? 'audit-remediation.integrations.continue' : 'audit-remediation.integrations.save')}</Button></div>
    </form>
  );
}

